<script setup>
// 스테이트먼트 — 반응형으로 옮긴 첫 섹션.
//
// 원본: Figma 홈(ver1) 4133:20411 (1920x774)
//   콘텐츠 4133:20412  x=365 y=200  1190x324  (좌우 365 / 위아래 225)
//   제목 3줄  50px bold  행간 68(=1.36)  검정  가운데
//   설명 2줄  24px medium 행간 35(=1.46)  투명도 50%  가운데
//   제목과 설명 사이 50px
//
// 원본에서 바꾼 것
//   1) 절대좌표(left:595px 같은 것)를 걷어내고 가운데 정렬 상자에 담았다.
//      섹션 높이 774 는 min-height 로 남겨 둔다 — 글이 접히면 그만큼 늘어난다.
//   2) 글자 크기를 clamp 로 감쌌다. 1280 이상에서는 원본 값(50 / 24px)에 걸려
//      더 커지지 않는다 — 1280 이든 2560 이든 완전히 같게 보인다.
//      1280 에서 3.9vw = 49.9px 이라 딱 그 지점에서 원본 크기가 된다.
//      데스크톱 화면은 768 까지 내려가는데(그 아래는 모바일 화면이 따로 있다),
//      50px 고정으로 두면 768 에서 제목이 석 줄로 접힌다. 그래서 아래쪽만 줄인다.
//   3) 줄바꿈을 keep-all 로 바꿨다. 원본은 한 줄에 다 들어가는 고정 폭이라
//      break-word 여도 상관없었지만, 이제 접힐 수 있으므로 낱말 중간에서
//      끊기지 않게 한다.
//
// 연출(줄마다 아래에서 올라오는 역커튼)은 원본 그대로다. 진행률로 바꾸는 것은
// 나중 단계다 — 지금은 배율 걷어내기만 한다.
import { computed, ref } from 'vue'
import { useDesignScale } from '@/composables/useDesignScale.js'
import {
  SCENE_REVEAL_DELAY_MS,
  STATEMENT_LINE_STAGGER_MS,
  TRANSITION_MS,
  sceneTextReveal,
} from '@/data/homeMotionTiming.js'

const props = defineProps({
  // 현재 스토리보드 프레임 번호 (홈1-N)
  step: { type: Number, default: 18 },
  // 배경을 끄고 나온다. /home2 는 섹션 뒤에 깔린 디졸브 배경이 색을 맡는다.
  bare: { type: Boolean, default: false },
})

// 세로 리듬 배율. 여백을 창 폭에 맞춰 줄인다 — 글자는 건드리지 않는다.
// 자세한 이유는 composables/useDesignScale.js 에 있다.
const root = ref(null)
const { scale: rhythm } = useDesignScale(root)

// 이 섹션이 뷰포트에 들어오는 첫 스텝
const FIRST_VISIBLE_FRAME = 16
const revealed = computed(() => props.step >= FIRST_VISIBLE_FRAME)

const LEAD = [
  '금과 은은 인류가 증명한 가장 오래된 REAL MONEY 입니다.',
  '자산이 흔들릴 때, 금과 은은 흔들리지 않습니다.',
  '실물은 곧 안전자산입니다.',
]
const SUB = [
  '골드나라는 금.은 거래에서 끝나지 않습니다.',
  '보증된 금 거래에서 자산관리와 그 가치의 성장까지 당신의 자산이 새로워 집니다.',
]

// 아래에서 위로 올라오며 드러나는 역커튼.
// 눈금선을 지나고 아주 잠깐(SCENE_REVEAL_DELAY_MS) 뒤에 시작한다.
//
// 두 번에 나눠 올린다(2026-09-25 요청) — 큰 글 세 줄이 한 덩어리로 올라오고,
// 작은 글이 뒤따른다. 줄마다 따로 올리면 다섯 번으로 보인다.
const curtain = (order) => sceneTextReveal(revealed.value, order)
</script>

<template>
  <!-- 좌우 여백은 섹션이 갖는다. 안쪽 글 상자가 원본의 1190 을 온전히 쓰게 하려는 것 —
       상자 쪽에 padding 을 주면 1280 창에서 제목 한 줄이 40px 모자라 두 줄로 접힌다. -->
  <section
    ref="root"
    class="flex w-full items-center justify-center px-[20px]"
    :style="{ '--r': rhythm }"
    :class="{ 'bg-white': !bare }"
    data-node-id="4133:20411"
  >
    <div class="mx-auto w-full max-w-[1190px] text-center" data-node-id="4133:20412">
      <!-- 큰 글 세 줄. 한 상자에 담아 한 덩어리로 올린다.
           상자를 줄마다 나누면 줄마다 올라오는 거리가 같아도 시작이 어긋나
           차례로 올라오는 것으로 보인다. -->
      <div class="overflow-hidden">
        <div class="motion-ease" :style="curtain(0)">
          <p v-for="(line, i) in LEAD" :key="`lead-${i}`" class="statement-lead">{{ line }}</p>
        </div>
      </div>

      <div class="mt-[50px] overflow-hidden">
        <div class="motion-ease" :style="curtain(1)">
          <p v-for="(line, i) in SUB" :key="`sub-${i}`" class="statement-sub">{{ line }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* 크기를 창 폭에 묶지 않는다. clamp 의 가운데 값(vw)은 좁은 화면에서만 작동하고,
   1280 을 넘으면 위쪽 값에 걸려 더 커지지 않는다. */
.statement-lead,
.statement-sub {
  margin: 0;
  word-break: keep-all;
}

.statement-lead {
  /* 어느 해상도에서도 같은 크기다(2026-09-26 요청).
     섹션이 통째로 줄어들 때(--fit) 제목만 그만큼 되돌려 상쇄한다. */
  font-size: calc(50px / var(--fit, 1));
  line-height: 1.36;
  font-weight: 700;
  color: #000;
}

.statement-sub {
  /* 24 / 1280 * 100 = 1.875vw. 위와 같은 이유로 조금 올려 잡는다. */
  font-size: 24px;
  line-height: 1.46;
  font-weight: 500;
  color: #000;
  opacity: 0.5;
}
</style>
